import React, { useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import FormInput from '../../../components/form/formInput';
import FormOptions from '../../../components/form/formOptions';
import { AutoFill } from '../../../components/field-autofill';

// Modules
import {
    ReservationById,
    Reservation,
    PriceConfirmationById,
    Address,
    POI,
    POIStatus,
    EtaWithGroups,
    AvailableDispatchGroup,
    RiderFavorites,
    DispatchGroup,
    AccountType,
    TParams,
} from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { actions as reservationActions, selectors as reservationSelectors } from '../../../redux/reducers/reservations';
import { actions as bookingActions, selectors as bookingSelectors } from '../../../redux/reducers/bookings';
import { actions as riderActions, selectors as riderSelectors } from '../../../redux/reducers/riders';
import { actions as driverActions, selectors as driverSelectors } from '../../../redux/reducers/drivers';
// Ressources
import ObjectUtils from '../../../utils/objectUtils';
import DateUtils from '../../../utils/dateUtils';
import FormUtils from '../../../utils/forms';
import AddressUtils from '../../../utils/addressUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import FormSubmit from '../../../components/form/formSubmit';
import { generateFormOptions, generateMadOptions } from '../../../utils/businessUtils';
import { getGeoAutocompleteURL, getListCompaniesMembershipURL, getListRidersURL } from '../../../utils/core-api';
import { FormDatas, FormPickerDatas } from '../../../local-types';

interface ReservationEditProps extends WithTranslation {
    match: match<TParams>;
    isLoadingOrders: boolean;
    isLoadingEditReservations: boolean;
    reservations: ReservationById;
    favorites: RiderFavorites;
    dispatchGroups: DispatchGroup[];
    poi: POI;
    poiStatus: POIStatus;
    etaWithGroups: EtaWithGroups;
    availableGroups: AvailableDispatchGroup[];
    priceConfirmation: PriceConfirmationById;
    getOrEditReservationError: ErrorPayload;
    getCreatePriceConfirmationError: ErrorPayload;
    getConfirmPriceConfirmationError: ErrorPayload;
    getReservation: (id: string) => void;
    editReservation: (id: string, payload: any) => void;
    clearPriceConfirmation: () => void;
    createPriceConfirmation: (payload: any) => void;
    clearReserver: () => void;
    getAvailableGroups: (payload: { lat: number; lng: number; profile: string; rider: number; date?: number }) => void;
    getEtaWithGroups: (payload: { lat: number; lng: number }) => void;
    getPoi: (payload: { lat: number; lng: number }) => void;
    checkFlight: (poiID, carrierCodeAndFlightNumber, time) => void;
    clearFlight: () => void;
    clearPickUp: () => void;
    getRiderFavorite: (id) => void;
    getDispatchGroups: () => any;
}

const ReservationEditPage = (props: ReservationEditProps): JSX.Element => {
    const id = props.match.params.id;

    const {
        t,
        dispatchGroups,
        reservations,
        favorites,
        poi,
        poiStatus,
        etaWithGroups,
        availableGroups,
        priceConfirmation,
        isLoadingOrders,
        isLoadingEditReservations,
        getOrEditReservationError,
        getCreatePriceConfirmationError,
        getConfirmPriceConfirmationError,
        getRiderFavorite,
        getPoi,
    } = props;

    const [reservation, setReservation] = React.useState<Reservation>(null);
    const [formData, setFormData] = React.useState<any>({});
    const [payload, setPayload] = React.useState<any>(null);
    const [formPoi, setFormPoi] = React.useState<any>({ number: null });

    useEffect(() => {
        props.getReservation(id);

        if (dispatchGroups.length === 0) {
            props.getDispatchGroups();
        }

        return () => {
            props.clearReserver();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reservations[id]) {
            const reserv = reservations[id];
            getRiderFavorite(reserv.booker.id);
            // console.log(reserv);
            if (reserv.planned_start_location) {
                reserv.planned_start_location.address.name = extractAddress(reserv.planned_start_location.address);
                getPoi(reserv.planned_start_location);
                getAvailableGroups(reserv.planned_start_location, {
                    is_pro: reserv.pro,
                    rider: reserv.booker.id,
                    start_date: reserv.planned_start_date * 1000,
                });
            }
            if (reserv.planned_end_location) {
                reserv.planned_end_location.address.name = extractAddress(reserv.planned_end_location.address);
            }
            if (reserv.trip_connection) {
                const poiload = AddressUtils.tripConnectionGetNumber(reserv.trip_connection);
                setFormPoi(poiload);
            }
            setReservation(reserv);
            const plannedDate = new Date(reserv.planned_start_date * 1000);
            const stringDate = DateUtils.dateFormat(plannedDate, "yyyy-mm-dd'T'HH:MM");
            const isMad = !!reserv.provision?.duration;
            const formload = {
                id: reserv.id,
                oldPu: reserv.planned_start_location?.address?.name,
                oldDo: reserv.planned_end_location?.address?.name,
                is_open: !isMad && !reserv.planned_end_location,
                oldRider: reserv.rider?.fullname,
                rider_id: reserv.rider?.id,
                dispatch_group: reserv.dispatch_group.id,
                start_date: stringDate,
                driver_additional_info: reserv.driver_additional_info || '',
                cost_center: reserv.cost_center?.id || '',
                reason: reserv.reason || '',
                poi_id: reserv.meeting_point?.id,
                trip_id: reserv.trip_connection?.id,
                is_pro: reserv.pro,
                book_for_others: reserv.book_for_others,
                is_mad: isMad,
                provision: '' + reserv.provision?.duration || '',
            };
            // console.log(formload);
            setFormData(formload);
            setPayload(formload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservations, getPoi, getRiderFavorite, id]);

    //==========================================//
    //=============== Generators ===============//
    //==========================================//

    const extractAddress = (obj: Address) => {
        return `${obj.street ?? ''} ${obj.postal_code ?? ''} ${obj.city ?? ''} ${obj.country ?? ''}`;
    };

    const getEtaFromClosest = (id) => {
        const res = etaWithGroups?.data?.[id]?.closest?.eta;
        return res ? `(${Math.ceil(res / 60)} min)` : '';
    };

    const getPrice = (id) => {
        let res = '';
        if (!isLoadingOrders && reservation.dispatch_group.id === id && reservation.order && !payload.is_open) {
            res = `${reservation.order?.base_price_with_vat} €`;
        }
        if (!ObjectUtils.isEmpty(priceConfirmation)) {
            const promo =
                priceConfirmation[id]?.formatted_total_price !== priceConfirmation[id]?.formatted_price
                    ? priceConfirmation[id]?.formatted_price
                    : null;
            res = `${priceConfirmation[id]?.formatted_total_price}${promo ? ' => ' + promo + ' ' + t('promo') : ''}`;
        }
        return res ? res : '';
    };

    const generateDispatchOptions = (): FormPickerDatas[] => {
        let optionsDispatch: FormPickerDatas[] = dispatchGroups.map((dg) => {
            return {
                id: dg.id,
                name: `${dg.name} ${getEtaFromClosest(dg.id)} ${getPrice(dg.id)}`,
            };
        });

        if (availableGroups?.length > 0) {
            const keys = availableGroups.map((g) => g.id);
            optionsDispatch = optionsDispatch.filter((o) => keys.includes(o.id));
        }

        if (priceConfirmation && Object.keys(priceConfirmation).length > 0) {
            const keys = Object.keys(priceConfirmation);
            optionsDispatch = optionsDispatch.filter((o) => keys.includes(o.id));
        }

        return optionsDispatch;
    };

    const generatePassengerOptions = (): FormPickerDatas[] => {
        const optionsDispatch: FormPickerDatas[] = [
            { id: 'me', name: t('reserver:form.me', { fullname: reservation.rider?.fullname }) },
            { id: 'company', name: t('reserver:form.company') },
            { id: 'guest', name: t('reserver:form.guest') },
        ];
        return optionsDispatch;
    };

    const generateCostCenters = (): FormPickerDatas[] => {
        let costCenters: FormPickerDatas[] = [];
        if (reservation?.company?.cost_centers) {
            costCenters = generateFormOptions(
                reservation.company.cost_centers.filter((cc) => cc.active),
                'id',
            );
        }
        return costCenters;
    };

    const generatePOIPicker = (): FormPickerDatas[] => {
        let options: FormPickerDatas[] = [];
        if (poi?.POI) {
            options = poi.POI.map(({ id, name, rdv_point }) => ({
                id: '' + id,
                name: `${name} (${rdv_point})`,
            }));
            // Remove all the other possibilities except the one of the POI.id
            // if (poiStatus?.POI?.id) {
            // options = options.filter((o) => o.id === '' + poiStatus.POI.id);
            // }
        }
        return options;
    };

    const generateInputs = (): FormDatas[] => {
        let timeToDestination = -1;
        let dateServer = new Date();
        let dateToDestination = new Date();
        // Compute time to destination
        const keysPC = Object.keys(priceConfirmation);
        if (priceConfirmation && keysPC.length > 0 && !payload?.is_asap) {
            dateServer = new Date(
                !payload?.is_asap && payload?.start_date
                    ? payload.start_date
                    : priceConfirmation[keysPC[0]].server_timestamp * 1000,
            );
            timeToDestination = Object.values(priceConfirmation)
                .map((p) => p.time)
                .reduce((a, b) => Math.max(a, b));
            dateToDestination = new Date(dateServer.getTime() + timeToDestination * 1000);
        }

        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        id: 'oldPu',
                        label: t('edit.pu'),
                        hint: t('edit.hint_pu'),
                        disable: true,
                    },
                    {
                        id: 'pu',
                        label: t('pu'),
                        customView: (val, handleChange) => {
                            return (
                                <AutoFill
                                    datas={{
                                        id: 'pu',
                                        label: t('edit.new_pu'),
                                        style: {
                                            minWidth: 200,
                                            width: '100%',
                                            flex: 1,
                                            marginRight: 8,
                                            paddingRight: 32,
                                        },
                                        variant: 'outlined',
                                        location: true,
                                        minChar: 3,
                                        required: false,
                                        onChange: handleChange,
                                        favorites: favorites,
                                        api: getGeoAutocompleteURL(),
                                    }}
                                />
                            );
                        },
                    },
                    {
                        id: 'oldDo',
                        label: t('edit.do'),
                        hint: t('edit.hint_do'),
                        disable: true,
                    },
                    {
                        id: 'do',
                        label: t('do'),
                        linked: { id: 'is_open', value: false },
                        customView: (val, handleChange) => {
                            return (
                                <AutoFill
                                    datas={{
                                        id: 'do',
                                        label: t('edit.new_do'),
                                        style: {
                                            minWidth: 200,
                                            width: '100%',
                                            flex: 1,
                                            marginRight: 8,
                                            paddingRight: 32,
                                        },
                                        variant: 'outlined',
                                        location: true,
                                        minChar: 3,
                                        required: false,
                                        onChange: handleChange,
                                        favorites: favorites,
                                        api: getGeoAutocompleteURL(),
                                    }}
                                />
                            );
                        },
                    },
                    {
                        id: 'is_open',
                        label: t('edit.is_open'),
                        linked: { id: 'is_mad', value: false },
                        type: 'boolean',
                    },
                    {
                        id: 'is_mad',
                        label: t('edit.is_mad'),
                        linked: { id: 'is_open', value: false },
                        type: 'boolean',
                    },
                    {
                        id: 'oldRider',
                        label: t('edit.rider'),
                        disable: true,
                    },
                    {
                        id: 'is_passenger_perso',
                        label: t('reserver:form.passenger'),
                        linked: formData.book_for_others ? [{ id: 'is_pro', value: false }] : [],
                        disable: true,
                        defaultValue: t('reserver:form.me', { fullname: reservation.rider?.fullname }),
                    },
                    {
                        id: 'is_passenger',
                        label: t('edit.new_rider'),
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'book_for_others', value: true },
                        ],
                        type: 'picker',
                        noEmpty: true,
                        options: generatePassengerOptions(),
                    },
                    {
                        id: 'rider',
                        label: t('edit.new_rider'),
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'is_passenger', value: 'company' },
                        ],
                        customView: (val, handleChange) => {
                            const companyId = reservation?.company?.id;
                            let api = getListRidersURL();
                            let customDatalist = null;
                            let onFind = null;
                            if (companyId) {
                                api = getListCompaniesMembershipURL(companyId);
                                customDatalist = (obj) => {
                                    const select = obj.rider;
                                    const value = `(${select.id}) ${select.fullname || select.name}`;
                                    return (
                                        <option key={select.id} value={value} data-id={select.id}>
                                            {value}
                                        </option>
                                    );
                                };
                                onFind = (datas, id) => {
                                    return datas.find((x) => x.rider && x.rider.id === id)?.rider;
                                };
                            }
                            return (
                                <AutoFill
                                    formControl={false}
                                    datas={{
                                        id: 'rider',
                                        name: 'rider[0]',
                                        label: t('edit.new_rider'),
                                        minChar: 1,
                                        style: {
                                            marginLeft: 16,
                                            minWidth: 200,
                                            width: '100%',
                                            flex: 1,
                                            marginRight: 8,
                                            paddingRight: 32,
                                        },
                                        variant: 'outlined',
                                        onChange: (idc, text, item, list, event) => {
                                            // setState({ idc, text });
                                            handleChange(event);
                                        },
                                        onFind: onFind,
                                        loaderPosition: 'right',
                                        api: api,
                                        customDatalist: customDatalist,
                                    }}
                                />
                            );
                        },
                    },
                    // Guest fields
                    {
                        id: 'guest[lastname]',
                        label: t('common:form.lastname'),
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'is_passenger', value: 'guest' },
                        ],
                        required: true,
                    },
                    {
                        id: 'guest[firstname]',
                        label: t('common:form.firstname'),
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'is_passenger', value: 'guest' },
                        ],
                        required: true,
                    },
                    {
                        id: 'guest[phone]',
                        label: t('common:form.phone_number'),
                        type: 'tel',
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'is_passenger', value: 'guest' },
                        ],
                        required: true,
                    },
                    {
                        id: 'guest[email]',
                        label: t('common:form.email'),
                        linked: [
                            { id: 'is_pro', value: true },
                            { id: 'is_passenger', value: 'guest' },
                        ],
                    },
                    {
                        id: 'dispatch_group',
                        label: t('recurrents.infos.vehicule_type_info'),
                        required: true,
                        options: generateDispatchOptions(),
                        noEmpty: true,
                        // The refresh price button shares the vehicle type line
                        customView: (val, handleChange, formDatas) => {
                            const options: FormPickerDatas[] = generateDispatchOptions();
                            return (
                                <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                                    {/* The column makes the picker stretch to its half of the line */}
                                    <div style={{ flex: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <FormOptions
                                            id="dispatch_group"
                                            name="dispatch_group"
                                            label={`${t('recurrents.infos.vehicule_type_info')} *`}
                                            value={val}
                                            options={options}
                                            optionsGroups={null}
                                            multipleOptions={options.length > 1}
                                            noEmpty
                                            required
                                            handleChange={handleChange}
                                        />
                                    </div>
                                    {!formDatas?.is_open && (
                                        <Button
                                            type="button"
                                            variant="contained"
                                            color="secondary"
                                            disabled={isLoadingOrders}
                                            onClick={() => refreshPrice(formDatas)}
                                            style={{ flex: 1, minWidth: 0, marginBottom: 8, marginRight: 16 }}
                                        >
                                            <Icon fontSize="small" style={{ marginRight: 8 }}>
                                                refresh
                                            </Icon>
                                            {t('edit.refresh_price')}
                                        </Button>
                                    )}
                                </div>
                            );
                        },
                    },
                    {
                        id: 'start_date',
                        label: t('common:date'),
                        required: true,
                        onBlurOnly: true,
                        type: 'datetime-local',
                        hint:
                            timeToDestination > 0
                                ? t('reserver:arrival_time', {
                                      date: DateUtils.dateFormat(dateToDestination.getTime(), 'dd/mm/yyyy HH:MM'),
                                      time: DateUtils.getFullDiff(
                                          t,
                                          DateUtils.dateDiff(dateServer, dateToDestination, { sec: true }),
                                      ),
                                  })
                                : timeToDestination === 0
                                  ? t('reserver:no_arrival_time')
                                  : null,
                    },
                    {
                        id: 'provision',
                        label: t('reserver:form.mad_time'),
                        type: 'picker',
                        noEmpty: true,
                        required: true,
                        linked: [{ id: 'is_mad', value: true }],
                        options: generateMadOptions(),
                    },
                ],
            },
            {
                title: t('reservation:recurrents.infos.options'),
                datas: [
                    // {
                    //     id: 'nameboard_option',
                    //     label: t('reservation:nameboard_option'),
                    //     type: 'boolean',
                    // },
                    { id: 'driver_additional_info', label: t('reservation:driver_additional_info') },
                    {
                        id: 'cost_center',
                        label: t('reserver:form.cost_center'),
                        options: generateCostCenters(),
                        required: reservation?.company?.cost_center_mandatory,
                        linked: { id: 'is_pro', value: true },
                    },
                    {
                        id: 'reason',
                        label: t('reserver:form.ref'),
                        required: reservation?.company?.ride_reason_mandatory,
                        linked: { id: 'is_pro', value: true },
                    },
                ],
            },
        ];

        if (!ObjectUtils.isEmpty(poi)) {
            let type, placeholder;
            switch (poi.mode) {
                case 'airport':
                    type = 'flight_number';
                    placeholder = 'AA000';
                    break;
                case 'station':
                    type = 'train_number';
                    placeholder = '34923';
                    break;
                default:
                    type = 'normal';
                    placeholder = '';
            }
            inputs.push({
                title: poi.name,
                datas: [
                    {
                        id: type,
                        label: t(`${type}`),
                        customView: () => {
                            if (type === 'normal') {
                                return null;
                            }
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flex: 1,
                                        }}
                                    >
                                        <FormInput
                                            id={type}
                                            name={type}
                                            type={'text'}
                                            placeholder={placeholder}
                                            value={formPoi?.[type]}
                                            label={t(`reservation:${type}`)}
                                            inputProps={{
                                                form: 'form_poi',
                                            }}
                                            required={true}
                                            disabled={false}
                                            handleChange={handleChangePoi}
                                        />
                                        {poi.mode === 'airport' && (
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                form="form_poi"
                                                style={{
                                                    height: 56,
                                                    alignSelf: 'flex-end',
                                                    marginBottom: 10,
                                                }}
                                                color={'secondary'}
                                            >
                                                <Icon fontSize="small">send</Icon>
                                            </Button>
                                        )}
                                    </div>
                                    {poiStatus && poiStatus.timezone && renderFlightDetail()}
                                </div>
                            );
                        },
                    },
                    {
                        id: 'poi_id',
                        label: poi.menuname,
                        options: generatePOIPicker(),
                        hint: t('reservation:poi_hint'),
                    },
                ],
            });
        }

        return inputs;
    };

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const generatePriceConfirmationPayload = (datas = null) => {
        const newFormData = datas ? datas : payload;

        if (
            !newFormData?.start_date ||
            (!newFormData?.pu && !reservation?.planned_start_location) ||
            (!newFormData?.do && !reservation?.planned_end_location && !newFormData.is_mad && !newFormData.is_open)
        ) {
            return null;
        }

        const startDate =
            newFormData && newFormData.start_date
                ? Math.trunc(new Date(newFormData.start_date).getTime() / 1000)
                : undefined;

        const pricePayload: any = {
            reservation: reservation.id,
            booker: reservation.booker.id,
            rider: reservation.rider.id,
            start_location: newFormData.pu
                ? AddressUtils.extractLoc(newFormData.pu)
                : reservation.planned_start_location,
            end_location: newFormData.do ? AddressUtils.extractLoc(newFormData.do) : reservation.planned_end_location,
            date: startDate,
            start_date: startDate,
            later: true,
            nameboard: 0,
            profile: reservation.pro ? AccountType.PRO : AccountType.PERSO,
        };

        if (newFormData.is_mad && newFormData.provision) {
            pricePayload.provision = { duration: parseInt(newFormData.provision) };
            if (!pricePayload.end_location) {
                delete pricePayload.end_location;
            }
        }

        if (newFormData.is_passenger !== 'guest') {
            pricePayload.rider = newFormData.rider_id;
        }

        return pricePayload;
    };

    const refreshPrice = (formDatas) => {
        if (formDatas?.is_open) {
            return;
        }
        props.clearPriceConfirmation();
        const dataPayload = generatePriceConfirmationPayload(formDatas);
        if (dataPayload) {
            props.createPriceConfirmation(dataPayload);
        }
    };

    const getAvailableGroups = (location, datas) => {
        const startDate =
            datas && datas.start_date ? Math.trunc(new Date(datas.start_date).getTime() / 1000) : undefined;

        const availablePayload: any = {
            lat: location.lat,
            lng: location.lng,
            profile: datas.is_pro ? AccountType.PRO : AccountType.PERSO,
            rider: datas.rider ?? reservation.booker.id,
        };
        if (startDate) {
            availablePayload.date = startDate;
        }
        props.getAvailableGroups(availablePayload);
    };

    const onChange = (newFormData) => {
        // console.log(newFormData);
        let rider_id_changed = false;
        if (newFormData.rider?.[0] && newFormData.rider[0].match(/\((\d*?)\).*/)) {
            const [, rider_id] = newFormData.rider[0].match(/\((\d*?)\).*/);
            rider_id_changed = payload.rider_id !== +rider_id;
        }

        if (!ObjectUtils.isEmpty(poi) && payload?.pu && !AddressUtils.isSameLocation(payload.pu, newFormData.pu)) {
            props.clearPickUp();
        }
        if (
            newFormData.pu &&
            (!payload ||
                !payload.pu ||
                payload.pu.place_id !== newFormData.pu.place_id ||
                !AddressUtils.isSameLocation(payload.pu, newFormData.pu))
        ) {
            const location = AddressUtils.extractLoc(newFormData.pu);
            props.clearPickUp();
            props.getEtaWithGroups(location);
            props.getPoi(location);
            getAvailableGroups(location, newFormData);
        }

        if (
            newFormData.pu &&
            payload &&
            (payload.start_date !== newFormData.start_date || payload.is_pro !== newFormData.is_pro)
        ) {
            const location = AddressUtils.extractLoc(newFormData.pu);
            getAvailableGroups(location, newFormData);
        }

        if (payload && payload.start_date !== newFormData.start_date && formPoi?.flight_number && poi?.id) {
            if (newFormData.start_date) {
                props.clearFlight();
                const startDate = Math.trunc(new Date(newFormData.start_date).getTime() / 1000);
                props.checkFlight(poi.id, formPoi.flight_number, startDate);
            }
        }

        if (newFormData.is_open) {
            props.clearPriceConfirmation();
        } else if (
            newFormData.is_mad !== payload.is_mad ||
            newFormData.provision !== payload.provision ||
            newFormData.is_open !== payload.is_open ||
            (!newFormData.pu && payload.pu) ||
            (!newFormData.do && payload.do) ||
            (newFormData.pu && !payload.pu) ||
            (newFormData.do && !payload.do) ||
            (newFormData.pu && payload.pu && payload.pu.place_id !== newFormData.pu.place_id) ||
            (newFormData.pu && payload.pu && !AddressUtils.isSameLocation(payload.pu, newFormData.pu)) ||
            (newFormData.do && payload.do && payload.do.place_id !== newFormData.do.place_id) ||
            (newFormData.do && payload.do && !AddressUtils.isSameLocation(payload.do, newFormData.do)) ||
            newFormData.start_date !== payload.start_date ||
            rider_id_changed ||
            (newFormData.dispatch_group !== payload.dispatch_group && !priceConfirmation[newFormData.dispatch_group])
        ) {
            props.clearPriceConfirmation();
            const dataPayload = generatePriceConfirmationPayload(newFormData);
            if (dataPayload) {
                props.createPriceConfirmation(dataPayload);
            }
        }
        setPayload(newFormData);
    };

    const handleSubmit = (datas: any, event: any): void => {
        // console.log('Sending', id, datas);

        if (datas.rider?.[0] && datas.rider[0].match(/\((\d*?)\).*/)) {
            const [, rider_id] = datas.rider[0].match(/\((\d*?)\).*/);
            datas.rider_id = +rider_id;
        } else {
            datas.rider_id = reservation.rider.id;
        }

        const poiDatas = {} as any;
        if (!ObjectUtils.isEmpty(poi)) {
            if (
                reservation.trip_connection?.flight_number &&
                `${reservation.trip_connection.carrier_IATA_code}${reservation.trip_connection.flight_number}` ===
                    formPoi.flight_number
            ) {
                poiDatas.trip_connection = { id: reservation.trip_connection.id };
            }

            if (poiStatus?.id && formPoi?.flight_number) {
                poiDatas.trip_connection = { id: poiStatus.id };
            }
            if (formPoi?.train_number) {
                poiDatas.trip_connection = { train_number: formPoi.train_number };
            }

            if (datas.poi_id) {
                poiDatas.poi_id = datas.poi_id;
            }

            if (poi?.POI?.length === 1) {
                poiDatas.poi_id = poi.POI[0].id;
            }
        }

        const changeDatas = { booker_id: reservation.booker.id, rider_id: datas.rider_id } as any;
        if (formData.driver_additional_info !== datas.driver_additional_info) {
            changeDatas.driver_additional_info = datas.driver_additional_info;
        }
        if (event.target.cost_center?.value) {
            changeDatas.cost_center = event.target.cost_center.value;
        }
        if (formData.reason !== datas.reason) {
            changeDatas.reason = datas.reason;
        }
        if (datas.is_passenger === 'guest') {
            changeDatas.guest = datas.guest;
        }
        if (formData.is_mad) {
            formData.is_open = false;
        }

        datas.dispatch_group = event.target.dispatch_group.value;

        if (!datas.is_open) {
            const pc =
                priceConfirmation && priceConfirmation[datas.dispatch_group]
                    ? priceConfirmation[datas.dispatch_group]
                    : reservation.order;
            props.editReservation(id, { order_id: pc.id, ...poiDatas, ...changeDatas });
        } else if (datas.is_open) {
            const freeload: any = {
                ...poiDatas,
                ...changeDatas,
                start_date: datas.start_date,
                planned_pickup_date: datas.start_date,
                dispatch_group: datas.dispatch_group,
                location: datas.pu ? AddressUtils.extractLoc(datas.pu) : reservation.planned_start_location,
            };
            if (!datas.is_open) {
                freeload.planned_end_location = datas.do
                    ? AddressUtils.extractLoc(datas.do)
                    : reservation.planned_end_location;
            }
            props.editReservation(id, freeload);
        }
        setFormData(datas);
    };

    const handleChangePoi = (event) => {
        setFormPoi(FormUtils.nextSubState(event, formPoi));
    };

    const handlePOINumberSubmit = (e) => {
        e.preventDefault();

        props.clearFlight();
        if (formPoi?.flight_number) {
            const startDate =
                payload && payload.start_date
                    ? Math.trunc(new Date(payload.start_date).getTime() / 1000)
                    : Math.trunc(new Date().getTime() / 1000);
            props.checkFlight(poi.id, formPoi.flight_number, startDate);
        }
    };

    //======================================//
    //=============== Render ===============//
    //======================================//

    const renderFlightDetail = () => {
        let labelColor = 'red';
        let flightDetail = t('reservation:flight.not_found');
        if (poiStatus.carrier_name) {
            const arrivalDate = DateUtils.tsToDate(poiStatus.planned_arrival_date, false);
            if (
                payload?.start_date &&
                poiStatus.planned_arrival_date * 1000 > new Date(payload.start_date)?.getTime()
            ) {
                flightDetail = `${t('reservation:flight.too_early')} (${arrivalDate})`;
            } else {
                labelColor = 'inherit';
                flightDetail = t('reservation:flight.found', {
                    carrier_name: poiStatus.carrier_name,
                    flight_number_and_carrier_name: poiStatus.flight_number_and_carrier_name,
                    terminal: poiStatus.arrival_terminal,
                    arrival_date: arrivalDate,
                });
            }
        }

        return (
            <div
                style={{
                    margin: '8px 16px',
                    color: labelColor,
                }}
            >
                {flightDetail}
            </div>
        );
    };

    return (
        <App noFav needRights={{ name: AccountRight.RIDE_DETAILS, mode: RightRank.W }}>
            <ErrorMessage payload={getOrEditReservationError} />
            <ErrorMessage payload={getConfirmPriceConfirmationError} />
            <ErrorMessage payload={getCreatePriceConfirmationError} />
            {formData && formData.id && (
                <>
                    <FormSubmit debounceSubmit={{ callback: handlePOINumberSubmit }} id="form_poi">
                        <input type="submit" hidden value="Ok" />
                    </FormSubmit>
                    <EnhancedForm
                        payload={formData}
                        inputs={generateInputs()}
                        backRoute={`${paths.reservations}/${id}`}
                        loader={isLoadingOrders || isLoadingEditReservations}
                        onChange={onChange}
                        onSubmit={handleSubmit}
                        submitHidden={
                            ObjectUtils.isEmpty(priceConfirmation) &&
                            !payload.is_open &&
                            payload.driver_additional_info === formData.driver_additional_info &&
                            payload.cost_center === formData.cost_center &&
                            payload.reason === formData.reason &&
                            String(payload.poi_id) === String(formData.poi_id) &&
                            payload.is_passenger === 'me' &&
                            (!poiStatus?.id || poiStatus?.id === formData.trip_id)
                        }
                        disableEnterSubmit={true}
                        submitDisabled={
                            payload?.start_date &&
                            poiStatus.planned_arrival_date * 1000 > new Date(payload.start_date)?.getTime()
                        }
                    />
                </>
            )}
        </App>
    );
};

const ReservationEditPageWithTranslation = withTranslation(['reservation', 'reserver', 'common'])(ReservationEditPage);

const mapStateToProps = (state) => ({
    favorites: riderSelectors.favorites(state),
    dispatchGroups: driverSelectors.sortedDispatchGroup(state),
    poi: bookingSelectors.poi(state),
    poiStatus: bookingSelectors.poiStatus(state),
    reservations: reservationSelectors.reservations(state),
    etaWithGroups: bookingSelectors.etaWithGroups(state),
    availableGroups: bookingSelectors.availableGroups(state),
    priceConfirmation: bookingSelectors.priceConfirmation(state),
    isLoadingOrders: pendingSelectors.isLoadingOrders(state),
    isLoadingEditReservations: pendingSelectors.isLoadingEditReservations(state),
    getOrEditReservationError: errorSelectors.getOrEditReservationError(state),
    getCreatePriceConfirmationError: errorSelectors.getCreatePriceConfirmationError(state),
    getConfirmPriceConfirmationError: errorSelectors.getConfirmPriceConfirmationError(state),
});

const mapDispatchToProps = {
    getReservation: reservationActions.getReservation,
    editReservation: reservationActions.editReservation,
    createPriceConfirmation: bookingActions.createPriceConfirmation,
    clearPriceConfirmation: bookingActions.clearPriceConfirmation,
    clearReserver: bookingActions.clearReserver,
    clearPickUp: bookingActions.clearPickUp,
    getAvailableGroups: bookingActions.getAvailableGroups,
    getEtaWithGroups: bookingActions.getEtaWithGroups,
    getRiderFavorite: riderActions.getRiderFavorite,
    getDispatchGroups: driverActions.getDispatchGroups,
    getPoi: bookingActions.getPoi,
    checkFlight: bookingActions.checkFlight,
    clearFlight: bookingActions.clearFlight,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReservationEditPageWithTranslation);
